// Bell button para a topbar — link para /notifications + badge de
// nao-lidas. Polling 60s via `useUnreadNotificationCountQuery`.
//
// Renderiza:
//   - Em estado normal: bell neutro.
//   - Quando count > 0: bell em primary ativo + badge vermelho com numero
//     (max 99+ para nao quebrar layout).
//
// Renderizado pela topbar (`src/app/(app)/_components/topbar.tsx`).

import { Bell } from 'lucide-react';
import { Link } from 'react-router-dom';

import { useUnreadNotificationCountQuery } from '../api/notifications.queries';

import { cn } from '@/shared/lib/cn';

export interface NotificationsBellProps {
  testID?: string;
}

function formatCount(count: number): string {
  if (count <= 0) return '';
  if (count > 99) return '99+';
  return String(count);
}

export function NotificationsBell({ testID = 'topbar-notifications' }: NotificationsBellProps) {
  const { data: unreadCount } = useUnreadNotificationCountQuery();
  const count = unreadCount ?? 0;
  const hasUnread = count > 0;
  const badgeText = formatCount(count);

  return (
    <Link
      to="/notifications"
      aria-label={hasUnread ? `Notificações (${count} não lidas)` : 'Notificações'}
      data-testid={testID}
      data-unread={hasUnread ? 'true' : 'false'}
      className={cn(
        'relative inline-flex h-11 w-11 items-center justify-center rounded-base transition-colors',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-canvas',
        hasUnread
          ? 'bg-primary/15 text-primary hover:bg-primary/20'
          : 'text-secondary-text hover:bg-surface-high hover:text-on-surface',
      )}
    >
      <Bell size={20} aria-hidden />
      {hasUnread ? (
        <span
          aria-hidden
          data-testid={`${testID}-badge`}
          className={cn(
            'absolute -right-0.5 -top-0.5 inline-flex min-w-[18px] items-center justify-center rounded-pill bg-danger px-1 text-[10px] font-extrabold text-white',
            'h-[18px] ring-2 ring-canvas',
          )}
        >
          {badgeText}
        </span>
      ) : null}
    </Link>
  );
}
